import { useState, useEffect } from 'react';
import { API_URL, API_KEY } from './constants';



/**
 * Handles API requests with consistent error handling and authentication
 */
const apiClient = {
  /**
   * Make a GET request to the API
   * @param {string} endpoint - The API endpoint (without the base URL)
   * @param {Object} options - Additional fetch options to override defaults
   * @returns {Promise<any>} - Parsed JSON response
   */
  async get(endpoint, options = {}) {
    return this.request(endpoint, {
      method: 'GET',
      ...options
    });
  },

  /**
   * Make a POST request to the API
   * @param {string} endpoint - The API endpoint (without the base URL)
   * @param {Object} data - The data to send in the request body
   * @param {Object} options - Additional fetch options to override defaults
   * @returns {Promise<any>} - Parsed JSON response
   */
  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      ...options
    });
  },

  /**
   * Make a POST request with FormData (for file uploads)
   * @param {string} endpoint - The API endpoint (without the base URL)
   * @param {FormData} formData - The FormData object
   * @param {Object} options - Additional fetch options to override defaults
   * @returns {Promise<any>} - Parsed JSON response
   */
  async postFormData(endpoint, formData, options = {}) {
    // Don't set Content-Type header - the browser will set it with the boundary
    const { headers, ...otherOptions } = options;

    return this.request(endpoint, {
      method: 'POST',
      body: formData,
      headers: {
        // Don't include Content-Type for FormData
        'X-API-Key': API_KEY,
        ...headers
      },
      ...otherOptions
    });
  },

  /**
   * Make a request to the API with consistent error handling
   * @param {string} endpoint - The API endpoint (without the base URL)
   * @param {Object} options - Fetch options
   * @returns {Promise<any>} - Parsed JSON response
   */
  async request(endpoint, options = {}) {
    const url = `${API_URL}${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`;
    console.log(url)
    const defaultHeaders = {
      'X-API-Key': API_KEY
    };

    // Add Content-Type only if not explicitly omitted for FormData
    if (!options.body || !(options.body instanceof FormData)) {
      defaultHeaders['Content-Type'] = 'application/json';
    }

    const config = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers
      }
    };

    try {
      const response = await fetch(url, config);

      // Handle HTTP errors
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.detail ||
          `API error: ${response.status} ${response.statusText}`
        );
      }

      // Parse JSON response
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }
};


/**
 * Hook for fetching data from the API
 * @param {string} endpoint - The API endpoint to fetch from
 * @param {Object} options - Additional fetch options
 * @param {Array} dependencies - Dependencies array for useEffect
 * @returns {Object} { data, loading, error, refetch }
 */
export const useApiGet = (endpoint, options = {}, dependencies = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiClient.get(endpoint, options);
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, dependencies);

  return { data, loading, error, refetch: fetchData };
};

/**
 * Hook for submitting data to the API
 * @returns {Object} { submitData, loading, error, data }
 */
export const useApiSubmit = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submitData = async (endpoint, data, method = 'post', options = {}) => {
    try {
      setLoading(true);
      setError(null);

      let result;
      if (method.toLowerCase() === 'post') {
        result = await apiClient.post(endpoint, data, options);
      } else if (method.toLowerCase() === 'postformdata') {
        result = await apiClient.postFormData(endpoint, data, options);
      } else {
        throw new Error(`Unsupported method: ${method}`);
      }

      setData(result);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { submitData, loading, error, data };
};

export default apiClient;
